import React, { useState, useMemo } from "react";
import { Calendar as CalendarIcon } from "lucide-react";
import { Button } from "@capacit-com/lara-law-shared-ui";
import { Calendar } from "@capacit-com/lara-law-shared-ui";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@capacit-com/lara-law-shared-ui";
import type { TimelineItem } from "@/models/legislation/Timeline";
import {
  getDateBounds,
  findAllDocsForDate,
  doesPathCrossBranch,
  getNavigationPathForDate,
  findNearestEarlierDateOnPath,
  findAllDocsForDateOnPath,
} from "./compactTimelineUtils";
import { formatDanishDate, parseSignatureDate } from "@/lib/timelineDates";

export type ZoomReason = "branch_crossing" | "multiple_docs";

type DateNavigatorProps = {
  root: TimelineItem;
  currentAccessionNumber: string;
  onNavigate: (accessionNumber: string) => void;
  onZoomOut: (
    date: Date,
    candidates: TimelineItem[],
    reason: ZoomReason
  ) => void;
};

const DateNavigator: React.FC<DateNavigatorProps> = ({
  root,
  currentAccessionNumber,
  onNavigate,
  onZoomOut,
}) => {
  const [open, setOpen] = useState(false);

  // Get current document's date for display
  const currentDate = useMemo(() => {
    const findCurrent = (node: TimelineItem): TimelineItem | null => {
      if (node.accessionNumber === currentAccessionNumber) return node;
      if (node.children) {
        for (const child of node.children) {
          const found = findCurrent(child);
          if (found) return found;
        }
      }
      return null;
    };
    const current = findCurrent(root);
    return current ? parseSignatureDate(current.signatureDate) : null;
  }, [root, currentAccessionNumber]);

  // Get min/max dates for calendar bounds
  const { minDate, maxDate } = useMemo(() => getDateBounds(root), [root]);

  // Handle date selection
  const handleSelect = (selectedDate: Date | undefined) => {
    if (!selectedDate) return;

    const navigationPath = getNavigationPathForDate(
      root,
      currentAccessionNumber,
      selectedDate
    );

    const nearestDate = findNearestEarlierDateOnPath(
      navigationPath,
      selectedDate
    );

    if (!nearestDate) {
      // No documents exist before this date on the current branch
      setOpen(false);
      return;
    }

    // Find matching documents on the current branch only
    const docsForDate = findAllDocsForDateOnPath(navigationPath, nearestDate);

    // Check if navigation would cross any branch points
    const pathCrossesBranch = doesPathCrossBranch(
      root,
      currentAccessionNumber,
      nearestDate
    );

    if (docsForDate.length === 1 && !pathCrossesBranch) {
      // Single document AND no branch crossing - direct navigation
      if (docsForDate[0].accessionNumber) {
        onNavigate(docsForDate[0].accessionNumber);
      }
    } else {
      const reason: ZoomReason =
        docsForDate.length > 1 ? "multiple_docs" : "branch_crossing";
      const zoomCandidates = pathCrossesBranch
        ? findAllDocsForDate(root, nearestDate)
        : docsForDate.length > 0
          ? docsForDate
          : findAllDocsForDate(root, nearestDate);
      onZoomOut(nearestDate, zoomCandidates, reason);
    }

    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-9 gap-2 text-sm font-normal"
        >
          <CalendarIcon className="h-4 w-4" />
          <span className="hidden sm:inline">
            {currentDate ? formatDanishDate(currentDate) : "Vælg dato"}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="p-3 border-b max-w-[280px]">
          <div className="text-sm font-medium">Gå til dato</div>
          <div className="text-xs text-muted-foreground mt-1 leading-relaxed">
            Vælg en dato for at navigere til den nærmeste tidligere version
          </div>
        </div>
        <Calendar
          mode="single"
          selected={currentDate || undefined}
          onSelect={handleSelect}
          disabled={(date) => {
            // Disable dates outside the tree's date range
            if (minDate && date < minDate) return true;
            if (maxDate && date > maxDate) return true;
            return false;
          }}
          defaultMonth={currentDate || undefined}
          captionLayout="dropdown"
          navigationFromYear={minDate?.getFullYear() ?? 1900}
          navigationToYear={maxDate?.getFullYear() ?? new Date().getFullYear()}
        />
      </PopoverContent>
    </Popover>
  );
};

export default DateNavigator;
